import * as Sentry from "@sentry/browser";
import { FormInstance, message, Table, Tag } from "antd";
import Card from "../components/Card";
import { ColumnsType } from "antd/lib/table/interface";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";

import { DownloadOutlined } from "@ant-design/icons";
import { PageToolbar } from "../components/PageToolbar";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import {
  BulkUpload,
  FileOption,
  User,
} from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  generateBulkUploadSourceTypes,
  generateBulkUploadStatusTypes,
  request,
  getStateColor,
  convertFileOptionsToSelectOptions,
} from "../helpers/utilities";

import { ALL_USERS_QUERY } from "../gql/users";
import { DEBOUNCE_TIME, PAGE_SIZE } from "../helpers/constants";
import {
  GET_BULK_UPLOADS,
  GET_BULK_UPLOAD_FILE_OPTIONS,
} from "../gql/bulk-uploads";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface BulkUploadsFilterParams {
  fileType: string;
  source: string;
  userId: string;
  status: string;
  url: string;
}

interface State {
  id: string;
  userId: string;
  users: User[];
  isLoading: boolean;
  status: string;
  bulkUploads: BulkUpload[];
  bulkUploadsCount: number;
  page: number;
  createdAt: string;
  source: string;
  url: string;
  pageSize: number;
  fileType: string;
  fileOptions: FileOption[];
  filterBy: string;
  isFiltering: boolean;
  filteredBulkUploads: BulkUpload[];
}

export function BulkUploadTableFactory(
  data: BulkUpload[],
  columns: ColumnsType<BulkUpload>,
  page?: number,
  pageSize?: number,
  count?: number,
  nextPage?: (page: number) => void,
  isLoading?: boolean,
): JSX.Element {
  const tempData: BulkUpload[] = data?.map((record: BulkUpload): BulkUpload => {
    return { ...record, id: record.id };
  });
  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: pageSize,
        total: count,
        onChange: nextPage,
      }}
    />
  );
}

export const columns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    render(text: string, record: BulkUpload): JSX.Element {
      return <span>{record.id}</span>;
    },
  },
  {
    title: "File Type",
    dataIndex: "fileType",
    key: "fileType",
    render(text: string, record: BulkUpload): JSX.Element {
      return <span style={{ textTransform: "capitalize" }}>{formatSnakeCase(record.fileType)}</span>;
    },
  },
  {
    title: "Generated By",
    dataIndex: "user",
    key: "user",
    align: "left" as const,
    render(text: string, record: BulkUpload): JSX.Element {
      return <span>{record?.user.name}</span>;
    },
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render(text: string, record: BulkUpload): JSX.Element {
      return (
        <span>
          {record.createdAt
            ? moment(record?.createdAt, "YYYY-MM-DD HH:mm:ss").format(
              "DD/MM/YYYY HH:mm",
            )
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    align: "left" as const,
    render(text: string, record: BulkUpload): JSX.Element {
      return (
        <Tag
          color={getStateColor(record?.state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(record?.state)}
        </Tag>
      );
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(text: string, record: BulkUpload): JSX.Element {
      return <span style={{ textTransform: "capitalize" }}>{formatSnakeCase(record?.source)}</span>;
    },
  },
  {
    title: "URL",
    dataIndex: "url",
    key: "url",
    align: "center" as const,

    render(text: string, record: BulkUpload): JSX.Element {
      return (
        <a href={`${record?.url}`} target="_blank" rel="noopener noreferrer">
          <DownloadOutlined />
        </a>
      );
    },
  },
  {
    title: "Error File URL",
    dataIndex: "errorFileUrl",
    key: "errorFileUrl",
    align: "center" as const,

    render(text: string, record: BulkUpload): JSX.Element {
      return (
        <>
          {record?.state === 'failed' && (
            <a
              href={record?.errorFileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DownloadOutlined />
            </a>
          )}
        </>
      );
    },
  },
];

export default class BulkUploads extends React.Component<
  RouteComponentProps,
  State
> {
  filterReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      status: "",
      userId: "",
      bulkUploads: [],
      bulkUploadsCount: 0,
      page: 1,
      users: [],
      pageSize: PAGE_SIZE,
      filterBy: "",
      isFiltering: false,
      filteredBulkUploads: [],
      id: "",
      url: "",
      fileType: "",
      createdAt: "",
      source: "",
      fileOptions: [],
    };

    this.filterReference = React.createRef<FormInstance>();
    this.getAllRecords = this.getAllRecords.bind(this);
    this.nextPage = this.nextPage.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.filterReference = ref;
  };

  getAllUsers = async (): Promise<void> => {
    try {
      const { data: { data } }: any = await request({
        query: ALL_USERS_QUERY,
        variables: {
          page: 1,
          q: this.state.filterBy,
        },
      });
      this.setState({
        users: data.users.results,
      });
    } catch (error) {
      // Fail silently or log error
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getFileOptions = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<{
        data: {
          fileOptionsForBulkUpload: FileOption[];
        };
      }> = await request({
        query: GET_BULK_UPLOAD_FILE_OPTIONS,
      });
      this.setState({
        fileOptions: data.data.fileOptionsForBulkUpload,
      });
    } catch (error) {
      // Fail silently
    }
  };

  getAllRecords = async (): Promise<void> => {
    const { fileType, status, page, source, pageSize, userId } = this.state;
    this.setState({ isLoading: true });
    try {
      const { data: { data } }: any = await request({
        query: GET_BULK_UPLOADS,
        variables: {
          page,
          numberPerPage: pageSize,
          fileType,
          source,
          state: status,
          userId,
        },
      });
      this.setState({
        bulkUploads: data.bulkUploadSearch.results,
        bulkUploadsCount: data.bulkUploadSearch.count,
      });
    } catch (error) {
      message.error(`Failed to load bulk uploads - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  async nextPage(page: number): Promise<void> {
    this.setState({ isLoading: true, page });
    await this.getAllRecords();
  }

  async componentDidMount(): Promise<void> {
    await Promise.all([
      this.getAllRecords(),
      this.getAllUsers(),
      this.getFileOptions(),
    ]);
  }


  showFilterButton = (): boolean => {
    const { userId, fileType, status, source, url } = this.state;
    return [userId, fileType, status, source, url].some(
      (filter) => filter !== undefined && String(filter).trim() !== "",
    );
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  filterBySource = (e: any): void => {
    const value = e?.target?.value ?? e;
    this.setState(
      {
        source: value,
        isLoading: true,
      },
      () => {
        this.getAllRecords();
      },
    );
  };

  filterByFiletype = (e: any): void => {
    const value = e?.target?.value ?? e;
    this.setState(
      {
        fileType: value,
        isLoading: true,
      },
      () => {
        this.getAllRecords();
      },
    );
  };

  filterByStatus = (e: any): void => {
    const value = e?.target?.value ?? e;
    this.setState(
      {
        status: value,
        isLoading: true,
      },
      () => {
        this.getAllRecords();
      },
    );
  };

  filterByUser = (e: any): void => {
    const value = e?.target?.value ?? e;
    this.setState(
      {
        userId: value,
        isLoading: true,
      },
      () => {
        this.getAllRecords();
      },
    );
  };

  removeFilters = async (): Promise<void> => {
    this.setState(
      {
        userId: "",
        fileType: "",
        status: "",
        source: "",
        url: "",
        isLoading: true,
      },
      () => {
        this.getAllRecords();
        if (this.filterReference.current) {
          this.filterReference.current.setFieldsValue({
            userId: undefined,
            fileType: undefined,
            status: undefined,
            source: undefined,
            url: undefined,
          });
        }
      },
    );
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ filterBy: searchTerm });
    await this.triggerDelayedSearch();
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      // status,
      bulkUploads,
      bulkUploadsCount,
      page,
      isFiltering,
      pageSize,
      // source,
      filteredBulkUploads,
    } = this.state;

    return (
        <Card>
          <div className="bulk-uploads-table-container">
            <PageToolbar modelName="bulk uploads"
              searchValue={this.state.filterBy}
              onSearchChange={(e) => this.setState({ filterBy: e.target.value }, this.triggerDelayedSearch)}
              searchPlaceholder="Search uploads..."
              totalCount={bulkUploadsCount}
              currentDataCount={bulkUploads.length}
              page={page}
              pageSize={pageSize}
              filters={
                <PageToolbar.Filters
                  isFiltering={this.showFilterButton()}
                  onClear={this.removeFilters}
                  configs={[
                    {
                      label: "User",
                      options: this.state.users.map(u => ({ value: u.id, label: u.name })),
                      selectedValues: this.state.userId ? [this.state.userId] : [],
                      onChange: (values) => this.filterByUser(values[0]),
                      onSearch: this.handleSearchInput,
                      loading: isLoading,
                      mode: "single",
                      width: 220,
                    },
                    {
                      label: "File Type",
                      options: convertFileOptionsToSelectOptions(this.state.fileOptions).map(o => ({ value: String(o.value ?? ""), label: o.name })),
                      selectedValues: this.state.fileType ? [this.state.fileType] : [],
                      onChange: (values) => this.filterByFiletype(values[0]),
                      mode: "single",
                      width: 220,
                    },
                    {
                      label: "Status",
                      options: generateBulkUploadStatusTypes().map(o => ({ value: String(o.value ?? ""), label: o.name })),
                      selectedValues: this.state.status ? [this.state.status] : [],
                      onChange: (values) => this.filterByStatus(values[0]),
                      mode: "single",
                      width: 180,
                    },
                    {
                      label: "Source",
                      options: generateBulkUploadSourceTypes().map(o => ({ value: String(o.value ?? ""), label: o.name })),
                      selectedValues: this.state.source ? [this.state.source] : [],
                      onChange: (values) => this.filterBySource(values[0]),
                      mode: "single",
                      width: 180,
                    },
                  ]}
                />
              }
            />
            {isFiltering
              ? BulkUploadTableFactory(
                filteredBulkUploads,
                columns,
                page,
                pageSize,
                bulkUploadsCount,
                this.nextPage,
                isLoading,
              )
              : BulkUploadTableFactory(
                bulkUploads,
                columns,
                page,
                pageSize,
                bulkUploadsCount,
                this.nextPage,
                isLoading,
              )}
          </div>
        </Card>
    );
  }
}
